import React, { ReactNode, useEffect, useState } from 'react';
import { Grid2, GridSize } from "@mui/material";
import { HighlightOffOutlined, Info, MailOutline, NearMeOutlined, SupervisorAccountOutlined } from '@mui/icons-material';
import { IUserSearchResult } from 'shared/models/api/models';
import { searchUserByName, getAllEmployees } from 'shared/services/userApi';
import { IDataCol, IKeyValuePair } from './DataGrid';
import { IListItemProps, List } from './Lists';
import DatePicker from 'react-datepicker';
import { LabelledCheckbox } from './LabelledCheckbox';
import { FormMode, IApplicationModule, IApplicationModuleViewAction } from './IApplicationModule';
import { IConfirmProps } from './ConfirmationBox';
import { useProfile, userHasPermission } from 'hooks/useRoles';

type EditLayout = "grid" | "form";
const DefaultLayout = "form";

const editStates = ["edit", "new", "quickEdit"] as FormMode[];
const viewStates = ["view", "review"] as FormMode[];

const allColumns = 12 as GridSize;
const propertyColumnSpan = 6 as GridSize;

// use the balance of the space for the value
const valueColumnSpan = (allColumns as number) - (propertyColumnSpan as number) as GridSize;

const titleAndControlOnSeperateRows = allColumns;
const titleAndControlOnSameRow = valueColumnSpan;

interface IFieldProps {
    heading: string,
    mode: FormMode,
    editModeValue: ReactNode,
    viewModeValue: ReactNode,
    isValid: boolean,
    validationMessages?: string[],
    immediateValidationMessages?: string[],
    validationTooltip?: string,
    editLayout?: EditLayout,
    tip?: string
}

const getLayout = (currentMode: FormMode, editLayout: EditLayout | undefined): EditLayout => {
    // always grid for view mode
    if (viewStates.includes(currentMode)) return "grid";

    return editLayout ?? DefaultLayout;
};

const getGridColSpanForLayout = (currentMode: FormMode, editLayout: EditLayout | undefined): GridSize => {
    if (getLayout(currentMode, editLayout) === "grid") {
        return titleAndControlOnSameRow; //50%
    }

    return titleAndControlOnSeperateRows;
};

const getValue = (fieldProps: IFormField) => {
    return (fieldProps.col.formatRow ? fieldProps.col.formatRow(fieldProps.row) : fieldProps.row[fieldProps.col.name]);
};

const getValues = (col: IDataCol) => {
    return (col.validation?.values ?? []) as IKeyValuePair[];
}

const getLookupKeyValuePair = (fieldProps: IFormField): IKeyValuePair => {
    const value = getValue(fieldProps);

    if (value !== null) {
        var kvp = getValues(fieldProps.col).find(v => v.key === value);

        if (kvp) {
            return kvp;
        }
    }

    return {} as IKeyValuePair;
};

const getLookupValue = (fieldProps: IFormField) => {
    return getLookupKeyValuePair(fieldProps)?.value;
};

const isReadonly = (currentMode: FormMode, col: IDataCol, row: any) => {
    // always readonly in view mode
    if (viewStates.includes(currentMode)) {
        return true;
    }

    // do we have a callback?
    if (col.readonlyIf) {
        if (col.readonlyIf(row) === true) {
            return true;
        }
    }

    // some controls can be set on new, but not updated, so we're not using editStates.includes(), just checking if edit
    if (col.readonlyForEdit === true && currentMode === "edit") {
        return true;
    }

    // use the static readonly flag, or default to false
    return col.readonly ?? false;
};

const getFieldMode = (fieldProps: IFormField): FormMode => {
    if (viewStates.includes(fieldProps.mode)) {
        return (fieldProps.mode);
    }

    // if we're in grid layout and its disabled, then just show as a label
    if (fieldProps.editLayout === "grid" && isReadonly(fieldProps.mode, fieldProps.col, fieldProps.row)) {
        return "view";
    }

    return fieldProps.mode;
};

export const Field = (props: IFieldProps) => {
    return (
        <Grid2 container className={"df-field-container" + (props.isValid ? " valid" : " invalid")}>
            <Grid2 size={{ md: propertyColumnSpan }} className="df-field-name">
                <span>
                    {props.heading}{(viewStates.includes(props.mode) ? ":" : "")}
                    {props.tip && editStates.includes(props.mode) &&
                        <span title={props.tip}>
                            <Info className="df-field-info-icon" />
                        </span>
                    }
                </span>
            </Grid2>

            <Grid2 size={{ md: propertyColumnSpan }} className="df-field-value">
                {viewStates.includes(props.mode) ? props.viewModeValue : props.editModeValue}

                {props.immediateValidationMessages != undefined && props.immediateValidationMessages?.map(message =>
                    <div className="validation-text">{message}</div>
                )}

                {!props.isValid && props.validationMessages?.map(message =>
                    <div className="validation-text">{message}</div>
                )}
            </Grid2>

        </Grid2>
    );
}

const UserListItem = (props: IListItemProps<IUserSearchResult>) =>
    <Grid2 container className="list-item user-list-item" onClick={() => props.toggle(props.item)}>
        <Grid2 size={{ xs: 11 }} className="name">{props.item.fullname}</Grid2>

        {props.item.city &&
            <Grid2 className="location"><NearMeOutlined /> {props.item.city}</Grid2>
        }

        {props.item.supervisor.length > 0 &&
            <Grid2 className="supervisor">
                <span><SupervisorAccountOutlined />{props.item.supervisor}</span>
            </Grid2>
        }

        {props.item.email.length > 0 &&
            <Grid2 className="email">
                <span><MailOutline />{props.item.email}</span>
            </Grid2>
        }
    </Grid2>;


interface IUserSearchProps {
    value: IUserSearchResult,
    onChange: (value: IUserSearchResult) => void,
    isExpanded: boolean,
    setIsExpanded: (expanded: boolean) => void,
    placeholderText: string
}

export const UserPicker = (props: IUserSearchProps) => {
    const [usersByNameSummary, setUsersByNameSummary] = useState("");
    const [users, setUsers] = useState([] as IUserSearchResult[]);
    const [searchText, setSearchText] = useState("");

    const blankUser = { id: null, fullname: "" } as IUserSearchResult;
    const defaultUser = { ...blankUser, id: props.value?.id || null, fullname: props.value?.fullname || "" };
    const [selectedUsers, setSelectedUsers] = useState(defaultUser ? [defaultUser] : [] as IUserSearchResult[]);

    const selectUser = (user: IUserSearchResult) => {
        setSelectedUsers([user]);
        props.onChange(user);
    }

    const filterUsersAsync = async (searchTerm: string, users?: IUserSearchResult[]): Promise<IUserSearchResult[]> => {
        const lcName = searchTerm.toLowerCase();

        const result = await (users ?? []).filter(u => {
            return (u.fullname.toLowerCase().indexOf(lcName) > -1
                || (u.email && u.email.toLowerCase().indexOf(lcName) > -1));
        });

        const usersText = result.length === 1 ? "user" : "users";

        setUsersByNameSummary(`${ result.length } ${ usersText } found for search "${ searchTerm }".`);

        return result;
    };

    useEffect(() => {
        let mounted = true;
        const mount = async () => {
            var users = await getAllEmployees();

            if (mounted) {
                setUsers(users.results);
            }
        };
        mount();
        return () => { mounted = false };
    }, []);

    return (
        <div className="df-user-select">
            <List<IUserSearchResult>
                summary={usersByNameSummary}
                dataSource={users}
                searchDelayMs={1}
                displayLoadingData={true}
                refreshFilterOnDataSourceChange={true}
                listItemType={UserListItem}
                filterText={searchText}
                setFilterText={setSearchText}
                expanded={props.isExpanded}
                setExpanded={props.setIsExpanded}
                buildKey={(item: IUserSearchResult) => { return item.id; }}
                filterAsync={filterUsersAsync}
                selected={selectedUsers}
                toggleSelected={selectUser}
                noResultsMessage="No users found"
                filterTextPlaceholder={props.placeholderText}
            />
            {!props.isExpanded && selectedUsers.length === 1 && selectedUsers[0]?.id && users.length > 0 &&
                <div className="df-selected-user">{selectedUsers[0].fullname}</div>
            }
        </div>
    );
};

interface IFormField {
    col: IDataCol,
    row: any,
    mode: FormMode,
    isValid: boolean,
    validationMessages?: string[],
    immediateValidationMessages?: string[],
    editLayout?: "grid" | "form",
    onChange: (newVal: any) => void,
    onBlur?: (newVal: any) => void,
    onPropertyChanged?: () => void
}

export const LabelField = (props: IFormField) => {
    const [currentValue, setCurrentValue] = useState(getValue(props));

    useEffect(() => {
        setCurrentValue(getValue(props));
    }, [props.row[props.col.name]]);

    return (
        <Field heading={props.col.heading}
            {...props}
            mode={"view"}
            tip={props.col.tip}
            validationTooltip={props.col.validation?.tip}
            viewModeValue={<div className="df-val-lbl">{currentValue}</div>}
            editModeValue={<div className="df-val-lbl">{currentValue}</div>}
        />
    );
};

export const KeyValueListItem = (props: IListItemProps<IKeyValuePair>) => {
    return (
        <div className={"list-item " + (props.selected ? " selected" : "")} onClick={() => props.toggle(props.item)}>
            <LabelledCheckbox checkedValue={props.selected === true}>
                <span className="name">{props.item.value}</span>
            </LabelledCheckbox>
        </div>
    );
};

export interface IKeyValueListProps {
    placeholder?: string,
    values: IKeyValuePair[],
    selected: IKeyValuePair[],
    setSelected: (val: IKeyValuePair[]) => void
}

export const KeyValueList = (props: IKeyValueListProps) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [filterText, setFilterText] = useState(props.selected?.length > 0 ? props.selected[0].value : "");

    const filterAsync = async (key: string): Promise<IKeyValuePair[]> => {
        return await props.values.filter(kvp => kvp.value.indexOf(key) > -1);
    };

    const toggle = (val: IKeyValuePair) => {
        setFilterText(val.value);
        props.setSelected(((val !== null) ? [val] : []))
    };

    return (
        <List<IKeyValuePair>
            dataSource={props.values}
            listItemType={KeyValueListItem}
            expanded={isExpanded}
            filterText={filterText}
            setFilterText={setFilterText}
            setExpanded={setIsExpanded}
            buildKey={item => item.key}
            filterTextPlaceholder={props.placeholder}
            noResultsMessage="No matching items"
            filterAsync={filterAsync}
            selected={props.selected !== null ? props.selected : []}
            toggleSelected={toggle}
            searchDisabled={true}
        />
    );
};

export const ComboField = (props: IFormField) => {
    const onChange = (selected: IKeyValuePair[]) => {
        props.onChange(selected?.length > 0 ? selected[0].key : null);
    };

    return (
        <Field heading={props.col.heading}
            {...props}

            mode={getFieldMode(props)}
            tip={props.col.tip}
            validationTooltip={props.col.validation?.tip}
            viewModeValue={<div className="df-val-lbl">{getLookupValue(props)}</div>}
            editModeValue={
                <KeyValueList
                    values={props.col.validation?.values ?? []}
                    selected={[getLookupKeyValuePair(props)]}
                    setSelected={onChange} />
            }
        />
    );
};

export const DateField = (props: IFormField) => {
    const [currentValue, setCurrentValue] = useState(getValue(props) as Date | null);

    const disabled = ((props.col.readonly ?? false) === true) || (props.mode === "edit" && ((props.col.readonlyForEdit ?? false) === true));

    const onChange = (newValue: Date | null) => {
        setCurrentValue(newValue);
        props.onChange(newValue);
    };

    useEffect(() => {
        setCurrentValue(getValue(props));
    }, [props.row[props.col.name]]);

    return (
        <Field heading={props.col.heading}
            {...props}
            mode={getFieldMode(props)}
            tip={props.col.tip}
            validationTooltip={props.col.validation?.tip}
            viewModeValue={
                <div className="df-val-lbl">{currentValue ? new Date(currentValue).toLocaleDateString() : ""} {currentValue ? new Date(currentValue).toLocaleTimeString() : ""}</div>
            }
            editModeValue={
                <DatePicker
                    dateFormat="dd/MM/yyyy"
                    disabled={disabled}
                    selected={currentValue ? new Date(currentValue) : null}
                    onChange={onChange} />
            }
        />
    );
};

const defaultMaxInputLength = 50;

export const TextField = (props: IFormField) => {
    const [currentValue, setCurrentValue] = useState(getValue(props));

    const disabled = ((props.col.readonly ?? false) === true) || (props.mode === "edit" && ((props.col.readonlyForEdit ?? false) === true));

    const onChange = (newValue: string) => {
        setCurrentValue(newValue);
        props.onChange(newValue);
    };

    useEffect(() => {
        setCurrentValue(getValue(props));
    }, [props.row[props.col.name]]);

    return (
        <Field heading={props.col.heading}
            {...props}
            mode={getFieldMode(props)}
            tip={props.col.tip}
            validationTooltip={props.col.validation?.tip}
            viewModeValue={
                <div className="df-val-lbl">
                    {currentValue}
                </div>
            }
            editModeValue={
                <input
                    type="text"
                    disabled={disabled}
                    maxLength={props.col.validation?.maxLength ?? defaultMaxInputLength}
                    placeholder={props.col.placeholderText ?? ""}
                    onChange={e => onChange(e.target.value)}
                    onBlur={e => props.onBlur && props.onBlur(e.target.value)}
                    value={currentValue ?? ""} />
            }
        />
    );
};

export const UserField = (props: IFormField) => {
    const [currentValue, setCurrentValue] = useState(getValue(props));

    const [expanded, setExpanded] = useState(false);

    const onChange = (newValue: IUserSearchResult) => {
        if (props.col.setValue) {
            setCurrentValue(newValue);
            props.onChange(newValue);
        }

        setExpanded(false);
    };

    useEffect(() => {
        setCurrentValue(getValue(props));
    }, [props.row[props.col.name]]);

    return (
        <>
            <Field heading={props.col.heading}
                {...props}
                mode={getFieldMode(props)}
                tip={props.col.tip}
                validationTooltip={props.col.validation?.tip}
                viewModeValue={<div className="df-val-lbl">{currentValue ? currentValue.fullname : ""}</div>}
                editModeValue={
                    <UserPicker
                        value={currentValue}
                        onChange={onChange}
                        isExpanded={expanded}
                        placeholderText={props.col.placeholderText ?? ""}
                        setIsExpanded={setExpanded}
                    />}
            />
        </>
    );
};

export const EmailField = (props: IFormField) => {
    const [currentValue, setCurrentValue] = useState(getValue(props));

    const disabled = isReadonly(props.mode, props.col, props.row);

    const onChange = (newValue: string) => {
        setCurrentValue(newValue);
        props.onChange(newValue);
    };

    useEffect(() => {
        setCurrentValue(getValue(props));
    }, [props.row[props.col.name]]);

    return (
        <Field heading={props.col.heading}
            {...props}
            mode={getFieldMode(props)}
            tip={props.col.tip}
            validationTooltip={props.col.validation?.tip}
            viewModeValue={
                <div className="df-val-lbl"><a target="_blank" href={"mailto:" + currentValue}>{currentValue}</a></div>
            }
            editModeValue={
                <input
                    type="text"
                    disabled={disabled}
                    maxLength={props.col.validation?.maxLength ?? defaultMaxInputLength}
                    placeholder={props.col.placeholderText ?? ""}
                    onChange={e => onChange(e.target.value)}
                    onBlur={e => props.onBlur && props.onBlur(e.target.value)}
                    value={currentValue ?? ""}
                />
            }
        />
    );
};

export const HyperlinkField = (props: IFormField) => {
    const [currentValue, setCurrentValue] = useState(getValue(props));

    const disabled = ((props.col.readonly ?? false) === true) || (props.mode === "edit" && ((props.col.readonlyForEdit ?? false) === true));

    const onChange = (newValue: string) => {
        setCurrentValue(newValue);
        props.onChange(newValue);
    };

    useEffect(() => {
        setCurrentValue(getValue(props));
    }, [props.row[props.col.name]]);

    return (
        <Field heading={props.col.heading}
            {...props}
            mode={getFieldMode(props)}
            tip={props.col.tip}
            viewModeValue={
                <div className="df-val-lbl">
                    <a target="_blank" href={props.row[props.col.name]}>
                        {props.row[props.col.name]}
                    </a>
                </div>
            }
            editModeValue={
                <input
                    type="text"
                    disabled={disabled}
                    maxLength={props.col.validation?.maxLength ?? defaultMaxInputLength}
                    placeholder={props.col.placeholderText ?? ""}
                    onChange={e => onChange(e.target.value)}
                    onBlur={e => props.onBlur && props.onBlur(e.target.value)}
                    value={currentValue ?? ""} />
            }
        />
    );
};

interface IDetailFormWrapperProps<T> {
    module: IApplicationModule,
    viewName: string,
    load?: () => Promise<T>,
    create?: () => T,
    save: (entity: T) => Promise<T>,
    setConfirmationProps: (props: IConfirmProps | null) => void,
    closeDialog: () => void,
    onDataChanged?: (row: T, msg: string | undefined) => void
}

export const DetailFormWrapper = <T,>(props: IDetailFormWrapperProps<T>) => {
    const msgBox = (msg: ReactNode) => {
        props.setConfirmationProps({
            message: msg,
            options: ["OK"],
            defaultOption: "OK",
            setResult: result => {
                props.setConfirmationProps(null);
            }
        });
    };

    const ask = (
        msg: string,
        opts: string[],
        def: string,
        defFn: () => void,
        otherFn?: (result: string) => void
    ) => {
        props.setConfirmationProps({
            message: msg,
            options: opts,
            defaultOption: def,
            setResult: result => {
                props.setConfirmationProps(null);

                if (result === def) {
                    if (defFn) defFn();
                } else {
                    if (otherFn) otherFn(result);
                }
            }
        });
    };


    return (
        <DetailsForm<T>
            module={props.module}
            load={props.load}
            create={props.create}
            cols={props.module.columns ?? []}
            save={props.save}
            setValid={() => { msgBox("Please enter all mandatory details in the form."); }}
            msgBox={msgBox}
            onDataChanged={props.onDataChanged}
            viewName={props.viewName ?? "view"}
            close={isDirty => {
                if (!isDirty) {
                    props.closeDialog();
                    return;
                }

                ask(
                    "Are you sure you want to exit? All your data will be lost.",
                    ["Exit", "Cancel"],
                    "Exit",
                    () => {
                        props.closeDialog();
                    });
            }}
        />
    );
};

interface IDetailsFormProps<T> {
    cols: IDataCol[],
    setValid: (isValid: boolean) => void,
    msgBox: (msg: ReactNode) => void,

    load?: () => Promise<T>,
    create?: () => T,
    save?: (data: T) => Promise<T>,
    close: (dirty?: boolean) => void,
    onDataChanged?: (row: T, msg: string | undefined) => void,
    viewName?: string,
    heading?: ReactNode,
    initialView?: FormMode,
    module?: IApplicationModule
}

export const DetailsForm = <T,>(props: IDetailsFormProps<T>) => {
    const currentUser = useProfile();
    const [data, setData] = useState(null as T | null);

    const [initialState, setInitialState] = useState("");
    const [dirtyState, setDirtyState] = useState(false);
    const [savingState, setSavingState] = useState("ready");
    const [ver, setVer] = useState(1);

    const [validationErrors, setValidationErrors] = useState([] as iInvalidColumn[]);
    const [immediateValidationErrors, setImmediateValidationErrors] = useState([] as iInvalidColumn[]);

    const findView = (name: string) => {
        return props.module?.views?.find(v => v.name === name)
            ?? props.module?.views?.find(v => v.name === "view");
    };

    const [appView, setAppView] = useState(findView(props.viewName ?? "view") ?? null);
    const [mode, setMode] = useState(appView?.mode ?? "view" as FormMode);

    useEffect(() => {
        const load = async () => {
            if (props.load) {
                var result = await props.load();

                if (result) {
                    setInitialState(JSON.stringify(result));
                    setData(result);
                }
            }
        }
        switch (mode) {
            case "quickEdit":
            case "view":
                load();
                break;
            case "new":
                if (props.create) {
                    const newRow = props.create();
                    setInitialState(JSON.stringify(newRow));
                    setData(newRow);
                }
                break;
        }
    }, []);

    const isDirty = () => {
        return (JSON.stringify(data) !== initialState);
    };

    const switchView = (viewName: string) => {
        var nextView = findView(viewName) ?? null;

        if (nextView) {
            setAppView(nextView);
            setMode(nextView.mode);
        } else {
            console.warn("cannot find view " + viewName);
        }

        setDirtyState(isDirty);
        setSavingState("ready");
    };

    interface iInvalidColumn {
        column: IDataCol,
        validationMessages?: string[]
    }


    const validateColumn = (col: IDataCol) => {
        const value = (data as any)[col.name] || '';
        const valueIsBlank = value === null || value === undefined || value == '';
        const rules = col.validation;

        var readonly = isReadonly(appView?.mode ?? "edit", col, data);

        if (readonly || !rules) {
            return null;
        }

        let validationMessages = [] as string[];

        let minLength = rules.minLength ?? 1;
        if (rules.required && valueIsBlank) {
            validationMessages.push('Required');
        }
        else if (value?.length < minLength) {
            validationMessages.push(`Required minimum length of ${ minLength }`);
        }

        let maxLength = rules.maxLength ?? defaultMaxInputLength;
        if (value?.length > maxLength) {
            validationMessages.push(`Must not exceed ${ maxLength } characters`);
        }

        if (rules.startsWith && rules.startsWith.length > 0 && (valueIsBlank || rules.startsWith.filter(ew => value.startsWith(ew)).length === 0)) {
            const startsWithString = rules.startsWith.reduce((prev, message) => `${ prev }${ prev ? ' or' : '' } ${ message }`);
            validationMessages.push(`Must start with ${ startsWithString }`);
        }

        if (rules.endsWith && rules.endsWith.length > 0 && (valueIsBlank || rules.endsWith.filter(ew => value.endsWith(ew)).length === 0)) {
            const endsWithString = rules.endsWith.reduce((prev, message) => `${ prev ? ' or' : '' } ${ message }`);
            validationMessages.push(`Must end with ${ endsWithString }`);
        }

        if (rules.segments && (valueIsBlank || value.split(rules.segments.splitter).length < rules.segments.min)) {
            validationMessages.push(rules.tip);
        }

        if (validationMessages.length > 0) {
            return {
                column: col,
                validationMessages: validationMessages
            } as iInvalidColumn;
        }

        return null;
    }

    const validate = () => {
        const invalidCols = [] as iInvalidColumn[];

        if (!data) return false;

        props.cols.filter(col => !!col.formComponent && col.validation).forEach(
            col => {
                const invalidColResult = validateColumn(col);
                if (invalidColResult) {
                    invalidCols.push(invalidColResult);
                }
            }
        );

        setValidationErrors(invalidCols);

        let isValid = invalidCols.length == 0 && immediateValidationErrors.length == 0;
        return isValid;
    };

    const validateImmediate = (col: IDataCol, value: string) => {
        if (col.validation?.immediateValidationFn) {
            col.validation?.immediateValidationFn(value).then((result) => validateImmediateResult(col, value, result));
        }
    }

    const validateImmediateResult = (col: IDataCol, value: string, valid: boolean) => {
        if (valid) {
            clearColImmediateResultError(col);
        }
        else {
            let msg = 'Validation issue';
            if (col.validation?.immediateValidationMsgFn) {
                msg = col.validation.immediateValidationMsgFn(value);
            }
            const filteredErrors = immediateValidationErrors.filter(e => e.column != col) || null;
            filteredErrors.push({ column: col, validationMessages: [msg] });
            setImmediateValidationErrors(filteredErrors);
        }
    }

    const clearColImmediateResultError = (col: IDataCol) => {
        const filteredErrors = immediateValidationErrors.filter(e => e.column != col) || null;
        setImmediateValidationErrors(filteredErrors);
    }

    const save = async (msg: string | undefined) => {
        if (props.save && savingState !== "working") {
            if (validate()) {
                setSavingState("working");

                var result = undefined as T | undefined;

                try {
                    result = await props.save(data as T);
                }
                catch {
                    props.msgBox("There was a problem saving.");
                }

                if (result) {
                    setSavingState("saved");
                    setInitialState(JSON.stringify(data));

                    if (props.onDataChanged) {
                        props.onDataChanged(data as T, msg);
                    }
                    return;
                }

                setSavingState("failed");
            } else {
                props.setValid(false);
            }
        }
    };

    const take = (action: IApplicationModuleViewAction) => {
        if (action.switchToView) {
            if (action.validate === true) {
                if (!validate()) {
                    return props.setValid(false);
                }
            }

            return switchView(action.switchToView as FormMode);
        }

        if (action.setResult) {
            switch (action.setResult) {
                case "save":
                    return save(action.msg);
                case "close":
                    return props.close(isDirty());
            }
        }
    };

    const HeadingIcon = appView?.subSubIcon;
    const Icon = appView?.icon;

    return (
        <>
            {appView?.heading &&
                <>
                    <div className="modal-heading">{appView.heading}</div>

                    <div className="modal-close-button" onClick={() => props.close(isDirty())}>
                        <HighlightOffOutlined />
                    </div>
                </>
            }

            {appView?.subHeading && <div className="modal-sub-header">
                <h3 className="modal-sub-header-bg">
                    <div className="tab-header">{appView.subHeading}</div>
                    <div className="modal-sub-heading-progress">{appView.progress}</div>
                </h3>
            </div>}

            {Icon &&
                <div className="modal-icon">
                    {Icon &&
                        <Icon />
                    }
                </div>
            }

            <div className="modal-body">
                <div className={"details-form df-" + getLayout(mode, appView?.editLayout ?? DefaultLayout) + "-layout df-" + mode + "-mode saving-" + savingState}>
                    {props.heading}

                    {appView?.subSubHeading && data &&
                        <div className="adl-heading">
                            {HeadingIcon && <HeadingIcon className="icon" />}
                            <span className="text">{appView.subSubHeading(data)}</span>
                        </div>
                    }

                    {data &&
                        <div className="df-table">
                            <div className="df-tbody">
                                {props.cols.filter(col => !!col.formComponent).map(col => {
                                    const FieldType = col.formComponent as (props: IFormField) => JSX.Element;

                                    const columnErrors = validationErrors.find(e => e.column == col) || null;
                                    const validationMessages = columnErrors?.validationMessages || [];

                                    const immediateColumnErrors = immediateValidationErrors.find(e => e.column == col) || null;
                                    const immediateValidationMessages = immediateColumnErrors?.validationMessages || [];

                                    return (<FieldType
                                        key={col.heading}
                                        col={col}
                                        row={data}
                                        mode={mode}
                                        editLayout={appView?.editLayout ?? DefaultLayout}
                                        isValid={!columnErrors && !immediateColumnErrors}
                                        validationMessages={validationMessages}
                                        immediateValidationMessages={immediateValidationMessages}
                                        onChange={value => {
                                            if (col.setValue) {
                                                col.setValue(data, value);
                                            }

                                            if (columnErrors != null) {
                                                validate();
                                            }

                                            if (immediateColumnErrors != null) {
                                                clearColImmediateResultError(col);
                                            }

                                            setVer(ver + 1);
                                        }}
                                        onBlur={(value) => { validateImmediate(col, value || '') }}
                                    />)
                                })}
                            </div>
                        </div>
                    }

                    <table>
                        <tbody>
                            <tr>
                                {appView?.actions &&
                                    appView.actions.filter(
                                        action => action.permission === null || userHasPermission(action.permission, currentUser?.roles ?? [])
                                    ).map(action =>
                                        <td key={action.name}>
                                            <button
                                                className={"modal-button " + ((appView?.defaultAction === action.name) ? "primary " : "") + action.name + "-button"}
                                                onClick={() => take(action)}>{action.label}</button>
                                        </td>
                                    )}
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    );
};